import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import FolderRoundedIcon from "@mui/icons-material/FolderRounded";
import HomeIcon from "@mui/icons-material/Home";
import { IconButton, Menu, MenuItem } from "@mui/material";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Button from "@mui/material/Button";
import SvgIcon from "@mui/material/SvgIcon";
import Typography from "@mui/material/Typography";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { useContext, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { setClientID, setProjectID } from "../../../../Redux/Actions/actions";
import { DashboardContext } from "../../DashboardContext/DashboardContextProvider";
import CreateFolderPopover from "./CreateFolderPopover/CreateFolderPopover";
import RenameFolderPopover from "./CreateFolderPopover/RenameFolderPopover";
import "./styles/project-file-structure.css";

// Component to display a specific project's folder
const ProjectFolder = () => {
  const {
    client,
    clientId,
    projectId,
    isAdmin,
    navigate,
    setHeading,
    queryClient,
  } = useContext(DashboardContext);

  const [userType, setUserType] = useState(isAdmin ? "admin" : "client");
  const [openAddFolder, setOpenAddFolder] = useState(false);
  const [contextMenu, setContextMenu] = useState(null);
  const [contextFolder, setContextFolder] = useState(null);
  const [openRenameFolder, setOpenRenameFolder] = useState(false);

  const dispatch = useDispatch();
  // Query to get all folders
  const {
    data: folders,
    isFetched,
    isLoading,
  } = useQuery({
    queryKey: ["all-project-structure", projectId, userType],
    queryFn: async () => {
      const res = await axios.get(
        `/api/all-project-structure/${projectId}/${userType}/`,
        {
          withCredentials: true,
        },
      );
      return res.data;
    },
    enabled: !!projectId,
  });

  useEffect(
    () =>
      setHeading(folders && isFetched ? folders.projectName : "Project Files"),
    [],
  );

  const handleContextMenu = (event, folder) => {
    event.preventDefault();
    setContextFolder(folder);
    setContextMenu(
      contextMenu === null
        ? {
            mouseX: event.clientX + 2,
            mouseY: event.clientY - 6,
          }
        : // repeated contextmenu when it is already open closes it with Chrome 84 on Ubuntu
          // Other native context menus might behave different.
          // With this behavior we prevent contextmenu from the backdrop to re-locale existing context menus.
          null,
    );
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
    setContextFolder(null);
  };

  return (
    <div className="project-files-container">
      <div className="folders-container">
        <div className="breadcrumbs-container">
          <Breadcrumbs
            separator="/"
            aria-label="breadcrumb"
            classes={{
              root: "breadcrumbs-root",
              separator: "breadcrumbs-separator",
            }}
          >
            {isAdmin && (
              <Button
                className="breadcrumbs-btn root-link-btn"
                variant="text"
                onClick={() => {
                  dispatch(setClientID(null));
                  dispatch(setProjectID(null));
                  navigate("/dashboard/files");
                }}
                startIcon={<HomeIcon id="root-folder-icon" />}
              >
                <Typography
                  variant="body1"
                  className="breadcrumbs-value root-text"
                >
                  Clients Folder
                </Typography>
              </Button>
            )}
            {!isAdmin && (
              <Button
                className="breadcrumbs-btn root-link-btn"
                variant="text"
                onMouseOver={async (e) => {
                  e.preventDefault();

                  await queryClient.prefetchQuery({
                    queryKey: ["client-structure", clientId],
                    queryFn: async () => {
                      const res = await axios.get(
                        `/api/client-structure/${clientId}/`,
                        {
                          withCredentials: true,
                        },
                      );
                      return res.data;
                    },
                    staleTime: 120000,
                  });
                }}
                onClick={() => {
                  dispatch(setProjectID(null));
                  navigate(`/dashboard/files/${clientId}`);
                }}
                startIcon={<HomeIcon id="root-folder-icon" />}
              >
                <Typography
                  variant="body1"
                  className="breadcrumbs-value root-text"
                >
                  {isFetched && folders.clientName
                    ? `${folders.clientName}`
                    : "Home Folder"}
                </Typography>
              </Button>
            )}
            {isAdmin && (
              <Button
                className="breadcrumbs-btn root-link-btn"
                variant="text"
                onMouseOver={async (e) => {
                  e.preventDefault();

                  await queryClient.prefetchQuery({
                    queryKey: ["client-structure", clientId],
                    queryFn: async () => {
                      const res = await axios.get(
                        `/api/client-structure/${clientId}/`,
                        {
                          withCredentials: true,
                        },
                      );
                      return res.data;
                    },
                    staleTime: 120000,
                  });
                }}
                onClick={() => {
                  dispatch(setProjectID(null));
                  navigate(`/dashboard/files/${clientId}`);
                }}
              >
                <Typography
                  variant="body1"
                  className="breadcrumbs-value root-text"
                >
                  {isFetched && folders.clientName
                    ? `${folders.clientName}`
                    : "Client Folder"}
                </Typography>
              </Button>
            )}
            <Typography
              variant="body1"
              className="breadcrumbs-value current-text"
            >
              {`${isFetched && folders.projectName ? folders.projectName : ""}`}
            </Typography>
          </Breadcrumbs>
        </div>
        {isFetched && folders.folders.length > 0
          ? folders.folders.map((folder) => (
              // <>
              <div
                key={folder.id}
                className={`folder-container`}
                onMouseOver={async (e) => {
                  e.preventDefault();
                  await queryClient.prefetchQuery({
                    queryKey: [
                      "all-project-files",
                      projectId,
                      folder.name,
                      userType,
                    ],
                    queryFn: async () => {
                      const response = await axios.get(
                        `/api/uploaded-project-files/${projectId}/${folder.name}/${userType}`,
                      );
                      return response.data;
                    },
                  });
                }}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(
                    `/dashboard/files/${clientId}/${projectId}/${folder.name}`,
                  );
                }}
                onContextMenu={(e) => {
                  if (folder.displayName === "Argus Generated Files") return;
                  console.log("Context menu for folder:", folder.displayName);
                  handleContextMenu(e, folder);
                }}
              >
                <SvgIcon
                  className="folder"
                  component={FolderRoundedIcon}
                  viewBox="3.6 3.6 16.8 16.8"
                  key={`${folder.displayName}-icon`}
                />
                <p className="folder-name" key={`${folder.displayName}-p`}>
                  {folder.displayName}
                </p>
              </div>
            ))
          : null}
        <Menu
          className="context-menu"
          open={contextMenu !== null}
          onClose={handleCloseContextMenu}
          anchorReference="anchorPosition"
          anchorPosition={
            contextMenu !== null
              ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
              : undefined
          }
          classes={{
            paper: "context-menu-paper",
            list: "context-menu-list",
          }}
        >
          <h2 id="context-menu-heading">Menu</h2>
          <MenuItem
            className="context-menu-item"
            id="rename-folder-menu-item"
            onClick={() => {
              setOpenRenameFolder(true);
            }}
          >
            <EditIcon id="rename-folder-icon" />
            Rename Folder
          </MenuItem>
          <MenuItem
            className="context-menu-item"
            id="delete-folder-menu-item"
            onClick={async (e) => {
              e.stopPropagation();
              if (!contextFolder) return;

              await axios.delete(
                `/api/delete-folder/${projectId}/${contextFolder.name}/`,
                {
                  withCredentials: true,
                },
              );
              await queryClient.invalidateQueries({
                queryKey: ["all-project-structure", projectId, userType],
                refetchType: "all",
              });
              handleCloseContextMenu();
              setContextFolder(null);
            }}
          >
            <DeleteIcon id="delete-folder-icon" />
            Delete Folder
          </MenuItem>
        </Menu>
        <IconButton
          id="upload-folder-icon-btn"
          onClick={() => setOpenAddFolder(true)}
        >
          <AddIcon className="new-folder-icon" id="add-folder-icon" />
        </IconButton>
      </div>
      <RenameFolderPopover
        open={openRenameFolder}
        setOpen={setOpenRenameFolder}
        projectId={projectId}
        queryClient={queryClient}
        userType={userType}
        folder={contextFolder}
      />
      <CreateFolderPopover
        open={openAddFolder}
        setOpen={setOpenAddFolder}
        projectId={projectId}
        queryClient={queryClient}
        userType={userType}
      />
    </div>
  );
};

export default ProjectFolder;
